import React, { useMemo } from 'react';

import { ContainsEditableProps, EditableType } from '@toasttab/sites-components';
import classnames from 'classnames';
import { Maybe } from 'graphql/jsutils/Maybe';

import { DoMenus_GetMenuItemsItem as DoMenusGetMenuItem } from 'src/apollo/onlineOrdering';

import LinkedItem from './LinkedItem';
import { GroupItemBackupContent, MenuGroupBlockConfig } from './types';

type TestProps = { testId?: string }

export type GroupedMenuItemsProps = {
  classNames?: string;
  config: MenuGroupBlockConfig | null
  styles?: any
  name: string;
  description: string;
  editableRef: EditableType<HTMLDivElement>
  itemsToRender: GroupItemBackupContent[] | Maybe<DoMenusGetMenuItem>[]
} & TestProps & ContainsEditableProps;

/*
  * Renders a list of menu items as a MenuGroup, regardless if items are related to each other.
  * Used in the MenuGroup, to be used in FeaturedItems.
*/
const GroupedMenuItems = (props: GroupedMenuItemsProps) => {
  const { editableRef, config, styles = {}, itemsToRender, name, description } = props;
  const columnsClass = useMemo(() => {
    const numColumns = config?.appearance?.columns || 1;
    return `columns${numColumns}`;
  }, [config?.appearance?.columns]);


  const dividerStyle = useMemo(() => {
    if(config?.appearance?.dividerOptions?.showDivider === false) {
      return {};
    }

    const { dividerColor, dividerWidth, dividerStyle } = config?.appearance.dividerOptions || {};

    const color = `${dividerColor || 'var(--ds-border-default)'}`;
    const style = dividerStyle || 'solid';
    const borderBottomWidth = `${dividerWidth ?? 1}px`;

    return { borderBottom: `${style} ${color}`, borderBottomWidth };
  }, [config?.appearance.dividerOptions]);


  const borderStyle = useMemo(() => {
    if(!config?.appearance?.borderOptions?.showBorder) {
      return {};
    }
    const { borderWidth, borderStyle, borderColor } = config?.appearance.borderOptions || {};
    const color = `${borderColor || 'var(--ds-border-default)'}`;
    const style = borderStyle || 'solid';
    const width = `${borderWidth ?? 1}px`;

    return { border: `${width} ${style} ${color}` };
  }
  , [config?.appearance.borderOptions]);

  const boxShadowStyle = useMemo(() => {
    if(config?.appearance?.borderOptions?.showBoxShadow === false) {
      return {};
    }
    return { boxShadow: '0 0 3px var(--ds-surface-shadow, rgba(0, 0, 0, 0.12))' };
  }, [config?.appearance.borderOptions?.showBoxShadow]);

  return (
    <div className="editableMenuGroupContainer" ref={editableRef}>
      <div className="groupContainer" style={{ ...styles, ...borderStyle, ...boxShadowStyle }}>
        <div className="groupWrapper">
          {config?.appearance?.showMenuGroupTitle !== false &&
              <div className="groupDetails">
                <div className="groupName">{name}</div>
                <div className="groupDescription">{description}</div>
              </div>}
          <div className={classnames('groupItems', columnsClass)}>
            {
              itemsToRender.filter(item => !!item)
                .map(item => {
                  return (
                    <div style={dividerStyle} key={item.guid}>
                      <LinkedItem item={item} {...props} />
                    </div>
                  );
                })
            }
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupedMenuItems;
